import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useStyles } from './MarketplaceTemplateFormFooter.styles'
import {
  ButtonV2,
  ModalConfirmV2,
  ModalSizeVariants,
} from '@labrav/react-components'
import { FooterV2 } from '../../../../common/components/FooterV2/FooterV2'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowRotateRight,
  faCircleXmark,
  faSave,
} from '@fortawesome/pro-regular-svg-icons'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from '../../../../store'
import { useUserType, useAccountId } from '@labrav/common'
import { MarketplaceTemplateDataResponse } from '../../../../modules/marketplaceMappingManager/types'
import { isEqual } from 'lodash'
import {
  getMarketplaceReferralTemplate,
  getMarketplaceReferralTemplateSalesforce,
  resetMarketplaceReferralTemplate,
  saveMarketplaceReferralTemplate,
  setMarketplaceTemplateAsDraft,
} from '../../../../modules/marketplaceMappingManager/action'
import { useTracker } from '@labrav/common'
import { UserProfile } from '@labrav/common'
import { CloudType } from '../../../../types/external'
import { CRMTypes } from '../../../../utils/constants'
import { LoadingTypes, isLoading as Loading } from '@labrav/common'

export const MarketplaceTemplateFormFooter: React.FC = () => {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const tracker = useTracker()
  const partnerType = useUserType()
  const { templateId = '' } = useParams()

  const crmId = useSelector<string>(
    state => state.AccountData[partnerType]?.partnerData?.crmId || ''
  )
  const crmType = useSelector<CRMTypes>(
    state => state.AccountData[partnerType]?.partnerData?.crmType as CRMTypes
  )
  const { cloudType } = useParams<{
    cloudType?: string
  }>()

  const cloudForAccountLookup = (cloudType || 'aws').toUpperCase() as CloudType
  const accountId = useAccountId({
    product: 'flyout',
    cloud: cloudForAccountLookup,
  })
  const user = useSelector<UserProfile | undefined>(
    state => state.userProfile.userProfile
  )
  const username = `${user?.givenName || ''} ${user?.familyName || ''}`.trim()
  const currentTemplateData = useSelector(
    state => state.marketplaceMappingManager.templateData.templateData
  )
  const isSaveLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.SAVE_REFERRAL_TEMPLATE)
  )
  const isDiscardLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.DISCARD_REFERRAL_TEMPLATE)
  )

  const [initialTemplateData, setInitialTemplateData] =
    useState<MarketplaceTemplateDataResponse>(currentTemplateData)
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false)
  const [openResetModal, setOpenResetModal] = useState<boolean>(false)
  const [openDiscardModal, setOpenDiscardModal] = useState<boolean>(false)
  const [openSaveModal, setOpenSaveModal] = useState<boolean>(false)

  const trackEvent = ({
    action,
    title,
    component = 'marketplace_template_form_footer',
    customData = {},
  }: {
    action: string
    title: string
    component?: string
    customData?: Record<string, any>
  }) => {
    tracker?.track({
      section: 'marketplace_mapping_manager_footer',
      component,
      action,
      title,
      partnerId: `${crmId}`,
      customerName: `${username}`,
      ...customData,
    })
  }

  const hasEmptyPicklistMappings = useMemo(() => {
    const fieldMappings = currentTemplateData?.fieldMappings || {}
    return Object.values(fieldMappings).some(
      field =>
        field.hubspotField &&
        (field.referralFieldType === 'Picklist' ||
          field.referralFieldType === 'Multipicklist') &&
        field.picklistMappings &&
        field.picklistMappings.length === 0
    )
  }, [currentTemplateData])

  const isSalesforce = crmType === CRMTypes.salesforce
  const isHubspot = crmType === CRMTypes.hubspot

  useEffect(() => {
    setInitialTemplateData(currentTemplateData)
  }, [currentTemplateData?.id])

  useEffect(() => {
    if (!initialTemplateData || !currentTemplateData) {
      setIsFormDirty(false)
      return
    }
    setIsFormDirty(
      !isEqual(
        initialTemplateData.fieldMappings,
        currentTemplateData.fieldMappings
      )
    )
  }, [currentTemplateData, initialTemplateData])

  const handleCloseResetModal = useCallback(() => {
    setOpenResetModal(false)
    trackEvent({
      action: 'reset-to-default-cancel',
      title: 'Reset to Default Modal Closed',
    })
  }, [])

  const handleResetButtonClick = useCallback(() => {
    trackEvent({
      action: 'reset-to-default-open',
      title: 'Reset to Default Modal Open',
    })
    setOpenResetModal(true)
  }, [])

  const handleResetMapping = useCallback(async () => {
    await dispatch(
      resetMarketplaceReferralTemplate(
        crmId,
        currentTemplateData.id,
        accountId,
        crmType
      )
    )
    setOpenResetModal(false)
    trackEvent({
      action: 'reset-to-default-confirm',
      title: 'Reset to Default Confirmed',
    })
  }, [crmId, currentTemplateData?.id, dispatch])

  const handleCloseDiscardModal = useCallback(() => {
    setOpenDiscardModal(false)
  }, [])

  const handleDiscardButtonClick = useCallback(() => {
    trackEvent({
      action: 'discard-open',
      title: 'Discard Modal Open',
    })
    setOpenDiscardModal(true)
  }, [])

  const handleSaveAsDraft = useCallback(async () => {
    await dispatch(setMarketplaceTemplateAsDraft(true))
    const savedAsDraft = (await dispatch(
      saveMarketplaceReferralTemplate(
        crmId,
        currentTemplateData.id,
        accountId,
        currentTemplateData?.useTemplateFromMappingService,
        crmType
      )
    )) as unknown as boolean
    if (savedAsDraft) {
      if (isSalesforce && cloudType) {
        navigate(
          `/settings/${cloudType.toLowerCase()}/${accountId}/mappings/marketplace`
        )
      } else {
        navigate('/settings/mappings/marketplace')
      }
    }
    setOpenDiscardModal(false)
    trackEvent({
      action: 'discard-save-as-draft',
      title: 'Save as Draft Clicked',
    })
  }, [
    crmId,
    currentTemplateData?.id,
    accountId,
    currentTemplateData?.useTemplateFromMappingService,
    crmType,
    dispatch,
    navigate,
  ])

  const handleDiscardMapping = useCallback(async () => {
    if (isHubspot) {
      await dispatch(getMarketplaceReferralTemplate(crmId, templateId, true))
    } else if (isSalesforce && accountId) {
      await dispatch(
        getMarketplaceReferralTemplateSalesforce(
          crmId,
          accountId,
          templateId,
          true
        )
      )
    }
    setOpenDiscardModal(false)
    setIsFormDirty(false)
    trackEvent({
      action: 'discard-confirm',
      title: 'Discard Confirmed',
    })
  }, [dispatch, initialTemplateData])

  const handleCancel = useCallback(() => {
    setOpenDiscardModal(false)
    trackEvent({
      action: 'discard-cancel-button',
      title: 'Discard Cancel Button Clicked',
    })
  }, [])

  const handleCloseSaveModal = useCallback(() => {
    setOpenSaveModal(false)
  }, [])

  const handleSaveButtonClick = useCallback(() => {
    setOpenSaveModal(true)
  }, [])

  const handleSaveMapping = useCallback(async () => {
    await dispatch(setMarketplaceTemplateAsDraft(false))
    const saved = (await dispatch(
      saveMarketplaceReferralTemplate(
        crmId,
        currentTemplateData.id,
        accountId,
        currentTemplateData?.useTemplateFromMappingService,
        crmType,
        cloudType
      )
    )) as unknown as boolean
    if (saved) {
      if (isSalesforce && cloudType) {
        navigate(
          `/settings/${cloudType.toLowerCase()}/${accountId}/mappings/marketplace`
        )
      } else {
        navigate('/settings/mappings/marketplace')
      }
    }
    setOpenSaveModal(false)
    trackEvent({
      action: 'save-confirm',
      title: 'Save Confirmed',
    })
  }, [
    crmId,
    currentTemplateData?.id,
    accountId,
    currentTemplateData?.useTemplateFromMappingService,
    crmType,
    dispatch,
    navigate,
  ])

  const footerActions = useMemo(
    () => (
      <div className={classes.footerWrapper}>
        <ButtonV2
          data-testid={'reset-to-default-btn'}
          styleType="link"
          onClick={handleResetButtonClick}
        >
          Reset to default
        </ButtonV2>
        <ButtonV2
          data-testid={'discard-btn'}
          styleType="outlined"
          onClick={handleDiscardButtonClick}
          disabled={!isFormDirty}
        >
          Discard
        </ButtonV2>
        <ButtonV2
          data-testid={'save-btn'}
          styleType="primary"
          onClick={handleSaveButtonClick}
          disabled={!isFormDirty}
        >
          Save
        </ButtonV2>
      </div>
    ),
    [
      classes.footerWrapper,
      handleDiscardButtonClick,
      handleResetButtonClick,
      handleSaveButtonClick,
      isFormDirty,
      hasEmptyPicklistMappings,
    ]
  )

  return (
    <div data-testid="marketplace-template-footer-container">
      <FooterV2 actions={footerActions} customClass={classes.footerRoot} />

      {/* Modals */}
      <ModalConfirmV2
        title="Reset to default mapping"
        icon={
          <FontAwesomeIcon
            icon={faArrowRotateRight}
            data-testid="reset-mapping-icon-modal"
          />
        }
        onlyCloseModal
        onCloseModal={handleCloseResetModal}
        onDecline={handleCloseResetModal}
        content={
          <div className={classes.heading}>
            Are you sure you want to reset the changes to default mapping? All
            your progress will be gone.
          </div>
        }
        open={openResetModal}
        titleCancelButton={'Cancel'}
        titleAcceptButton={'Reset'}
        onAccept={handleResetMapping}
        variant={ModalSizeVariants.medium}
        hideCancelButton={false}
        acceptLoading={isDiscardLoading}
      />

      <ModalConfirmV2
        title="Discard changes"
        icon={
          <FontAwesomeIcon
            icon={faCircleXmark}
            data-testid="discard-changes-icon-modal"
          />
        }
        onlyCloseModal
        onCloseModal={handleCloseDiscardModal}
        onDecline={handleSaveAsDraft}
        content={
          <div className={classes.heading}>
            If you discard changes, all your progress will be lost. Are you sure
            you want to continue?
          </div>
        }
        open={openDiscardModal}
        footerActions={
          <ButtonV2
            onClick={handleCancel}
            data-testid="cancelButton"
            styleType="outlined"
            smallVariant
          >
            Cancel
          </ButtonV2>
        }
        titleCancelButton={'Save as draft'}
        titleAcceptButton={'Discard'}
        onAccept={handleDiscardMapping}
        variant={ModalSizeVariants.medium}
        hideCancelButton={false}
        acceptLoading={isDiscardLoading}
      />

      <ModalConfirmV2
        title="Save configuration"
        icon={
          <FontAwesomeIcon
            icon={faSave}
            data-testid="save-changes-icon-modal"
          />
        }
        onlyCloseModal
        onCloseModal={handleCloseSaveModal}
        onDecline={handleCloseSaveModal}
        content={
          <div className={classes.heading}>
            Are you sure you want to save this configuration?
          </div>
        }
        open={openSaveModal}
        titleCancelButton={'Cancel'}
        titleAcceptButton={'Save'}
        onAccept={handleSaveMapping}
        variant={ModalSizeVariants.medium}
        hideCancelButton={false}
        acceptLoading={isSaveLoading}
      />
    </div>
  )
}
